import React, { useCallback, useEffect, useRef, useState } from "react";

function displayRect(img) {
  if (!img || !img.naturalWidth) return null;
  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  const cw = img.clientWidth;
  const ch = img.clientHeight;
  const scale = Math.min(cw / nw, ch / nh);
  const dw = nw * scale;
  const dh = nh * scale;
  return {
    left: (cw - dw) / 2,
    top: (ch - dh) / 2,
    dw,
    dh,
  };
}

function eventToBoxPoint(event, img) {
  const layout = displayRect(img);
  if (!layout || layout.dw < 1 || layout.dh < 1) return null;
  const rect = img.getBoundingClientRect();
  const x = (event.clientX - rect.left - layout.left) / layout.dw;
  const y = (event.clientY - rect.top - layout.top) / layout.dh;
  return {
    x: Math.min(1, Math.max(0, x)),
    y: Math.min(1, Math.max(0, y)),
  };
}

export default function ImagineRegionEditor({ src, boxes, onChange, disabled }) {
  const imgRef = useRef(null);
  const [draft, setDraft] = useState(null);
  const [selected, setSelected] = useState(null);
  const [frame, setFrame] = useState(null);

  const measure = useCallback(() => {
    const layout = displayRect(imgRef.current);
    setFrame(layout);
  }, []);

  const redrawSafe = useCallback(() => {
    setSelected((cur) => (cur != null && cur >= (boxes || []).length ? null : cur));
  }, [boxes]);

  useEffect(() => {
    redrawSafe();
  }, [redrawSafe]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, src]);

  const startDrag = (event) => {
    if (disabled || !src) return;
    const img = imgRef.current;
    const point = eventToBoxPoint(event, img);
    if (!point) return;
    event.preventDefault();
    setSelected(null);
    setDraft({ x: point.x, y: point.y, w: 0, h: 0, ox: point.x, oy: point.y });
  };

  useEffect(() => {
    if (!draft) return undefined;
    const onMove = (event) => {
      const img = imgRef.current;
      const point = eventToBoxPoint(event, img);
      if (!point) return;
      const x = Math.min(draft.ox, point.x);
      const y = Math.min(draft.oy, point.y);
      setDraft((d) =>
        d
          ? {
              ...d,
              x,
              y,
              w: Math.abs(point.x - d.ox),
              h: Math.abs(point.y - d.oy),
            }
          : d,
      );
    };
    const onUp = () => {
      setDraft((d) => {
        if (d && d.w >= 0.02 && d.h >= 0.02) {
          onChange([...(boxes || []), { x: d.x, y: d.y, w: d.w, h: d.h }]);
        }
        return null;
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [draft, boxes, onChange]);

  const shown = [...(boxes || [])];
  if (draft && draft.w > 0 && draft.h > 0) shown.push(draft);

  const layoutForCss = (box) => {
    if (!frame) {
      return {
        left: `${box.x * 100}%`,
        top: `${box.y * 100}%`,
        width: `${box.w * 100}%`,
        height: `${box.h * 100}%`,
      };
    }
    return {
      left: `${frame.left + box.x * frame.dw}px`,
      top: `${frame.top + box.y * frame.dh}px`,
      width: `${box.w * frame.dw}px`,
      height: `${box.h * frame.dh}px`,
    };
  };

  return (
    <div className="space-y-2">
      <div className="relative w-full bg-black/40 overflow-hidden select-none">
        {src ? (
          <img
            ref={imgRef}
            src={src}
            alt="Photo to fix"
            draggable={false}
            onPointerDown={startDrag}
            onLoad={measure}
            className="block w-full max-h-[420px] object-contain cursor-crosshair"
          />
        ) : (
          <div className="h-48 flex items-center justify-center text-sm font-['Manrope'] text-white/40">
            Select a photo to mark areas
          </div>
        )}
        {src && (
          <div className="absolute inset-0 pointer-events-none">
            {shown.map((box, idx) => (
              <div
                key={`${box.x}-${box.y}-${idx}`}
                className={`absolute border-2 ${
                  selected === idx ? "border-blue-400 bg-blue-400/20" : "border-amber-400 bg-amber-400/15"
                }`}
                style={layoutForCss(box)}
              />
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {(boxes || []).map((box, idx) => (
          <button
            key={`${box.x}-${idx}`}
            type="button"
            disabled={disabled}
            onClick={() => setSelected(idx)}
            className={`min-h-11 px-3 text-xs font-['Manrope'] border ${
              selected === idx
                ? "border-blue-400 text-blue-200"
                : "border-white/15 text-white/70 hover:bg-white/5"
            }`}
          >
            Box {idx + 1}: {Math.round(box.x * 100)}%,{Math.round(box.y * 100)}%{" "}
            {Math.round(box.w * 100)}×{Math.round(box.h * 100)}
          </button>
        ))}
        {(boxes || []).length > 0 && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              if (selected == null) onChange([]);
              else onChange((boxes || []).filter((_, i) => i !== selected));
              setSelected(null);
            }}
            className="min-h-11 px-3 text-xs font-['Manrope'] border border-white/15 text-white/70 hover:bg-white/5"
          >
            {selected == null ? "Clear boxes" : "Remove selected"}
          </button>
        )}
      </div>
      <p className="text-xs font-['Manrope'] text-white/40">
        Drag on the photo to mark the area to fix. Boxes guide the model; review the result before adding it to Media.
      </p>
    </div>
  );
}
